"use client";

import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  CloseOutlined,
  SoundOutlined,
  AudioMutedOutlined,
  StopOutlined,
  FullscreenOutlined,
} from "@ant-design/icons";
import { useEffect, useState, type FC } from "react";
import { useAudio } from "../../context/AudioProvider";
import { useNavigate } from "react-router-dom";

const MiniAudioPlayer: FC = () => {
  const navigate = useNavigate();
  const {
    isPlaying,
    togglePlay,
    currentTime,
    currentMusic,
    duration,
    seekAudio,
    audioState,
    audioRef,
    setAudioState,
    currentPlayerUrl,
  } = useAudio();

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [progress, setProgress] = useState(0);
  const [scrollThreshold, setScrollThreshold] = useState(20); // Default for phone

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);

      if (width < 768) {
        setScrollThreshold(20); // Phone
      } else if (width >= 768 && width <= 1024) {
        setScrollThreshold(30); // Laptop
      } else {
        setScrollThreshold(40); // Desktop
      }
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initialize on mount

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update progress percentage for visual indicator
  useEffect(() => {
    if (duration > 0) {
      setProgress((currentTime / duration) * 100);
    }
  }, [currentTime, duration]);

  // Handle stop button click
  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      seekAudio(0);
      setAudioState("stopped");
    }
  };

  if (audioState === "stopped") {
    return <></>;
  }

  return (
    <>
      <div
        className={`fixed opacity-0.5  ${
          isExpanded ? "w-full left-0 right-0 px-4" : "right-4 max-w-[400px]"
        } bg-amber-950 text-white p-2 rounded-lg shadow-lg flex items-center space-x-4 z-[10000000000000] transition-all duration-300`}
      >
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-lg hover:text-gray-300 transition-colors"
          aria-label={isExpanded ? "Collapse player" : "Expand player"}
        >
          {isExpanded ? (
            <CloseOutlined />
          ) : isPlaying ? (
            <SoundOutlined />
          ) : (
            <AudioMutedOutlined />
          )}
        </button>

        {isExpanded && (
          <>
            <div className="flex flex-col w-full space-y-2">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center space-x-4 px-2">
                  <img
                    src={
                      currentMusic?.coverImage || "/images/homePage/book2.svg"
                    }
                    alt={currentMusic?.title || "Audio Book"}
                    className="w-12 h-12 rounded-lg shadow-lg"
                  />
                  <div className="relative w-32 md:w-60 lg:w-80 overflow-hidden whitespace-nowrap">
                    <p
                      className="text-sm font-semibold text-center inline-block animate-scroll"
                      style={{
                        animation:
                          (currentMusic?.title?.length || 0) > scrollThreshold
                            ? "scrollText 10s linear infinite"
                            : "none",
                      }}
                    >
                      {currentMusic?.title || "No Title"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4 py-2 px-2">
                  <button
                    onClick={togglePlay}
                    className="text-white text-lg hover:text-gray-300 transition-colors"
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <PauseCircleOutlined />
                    ) : (
                      <PlayCircleOutlined />
                    )}
                  </button>

                  <button
                    onClick={handleStop}
                    className="text-white text-lg hover:text-gray-300 transition-colors"
                    aria-label="Stop"
                  >
                    <StopOutlined />
                  </button>

                  <div className="text-xs">
                    <span>
                      {new Date(currentTime * 1000)
                        .toISOString()
                        .substring(14, 19)}
                    </span>{" "}
                    /
                    <span>
                      {new Date(duration * 1000)
                        .toISOString()
                        .substring(14, 19)}
                    </span>
                  </div>

                  <button
                    onClick={() => navigate(currentPlayerUrl)}
                    className="text-white text-lg hover:text-gray-300 transition-colors pr-1"
                    aria-label="Fullscreen"
                  >
                    <FullscreenOutlined />
                  </button> &nbsp; &nbsp;
                </div>
              </div>

              <div className="relative w-full h-2 bg-gray-700 rounded-full mb-4">
                <div
                  className="absolute top-0 left-0 h-full bg-white rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onChange={(e) => seekAudio(Number.parseFloat(e.target.value))}
                  className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MiniAudioPlayer;
